/*
 * SearchableSelect — Notion / Linear-style dropdown.
 *
 * - Click the trigger to open a popover with a search box + list of options.
 * - Optional `renderIcon(option)` to draw a leading icon per row.
 * - `onCreateOption(query) → option`  enables "+ Create '<query>'" inline.
 * - `multi`: keeps the popover open and toggles selection.
 *
 * Kept dependency-free so the same component can be reused for assignee,
 * priority, and labels pickers.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaSearch, FaCheck, FaChevronDown, FaPlus, FaTimes } from 'react-icons/fa';
import './reminders.css';

// Buttons in here use the `.reminders-*-btn` helper classes from
// reminders.css. Those use `!important` to neutralise the host CMS's global
// orange `button {}` rule without touching our own primary CTAs.

const triggerCls = 'reminders-trigger-btn flex w-full items-center justify-between gap-2 text-sm';

function SearchableSelect({
  value,
  options, // [{ value, label, sublabel? }]
  onChange,
  multi = false,
  placeholder = 'Select',
  emptyLabel = 'No matches',
  renderIcon,
  renderRow,
  onCreateOption,
  onQueryChange, // fires whenever the search input changes — enables async loading
  asyncOptions = false, // when true, do not client-filter — parent owns options
  clearable = false,
  triggerClassName,
  triggerNode, // optional fully-custom trigger
  popoverClassName // override popover sizing — e.g. 'min-w-[360px]' or 'w-[420px]'
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click / escape
  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (!open) setQuery('');
  }, [open]);

  const selectedSet = useMemo(() => {
    if (multi) return new Set(Array.isArray(value) ? value : []);
    return new Set(value !== undefined && value !== null && value !== '' ? [value] : []);
  }, [value, multi]);

  const filtered = useMemo(() => {
    if (asyncOptions) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        (o.label || '').toLowerCase().includes(q) || (o.sublabel || '').toLowerCase().includes(q)
    );
  }, [options, query, asyncOptions]);

  useEffect(() => {
    if (typeof onQueryChange === 'function') onQueryChange(query);
  }, [query, onQueryChange]);

  const canCreate =
    typeof onCreateOption === 'function' &&
    query.trim().length > 0 &&
    !options.some((o) => (o.label || '').toLowerCase() === query.trim().toLowerCase());

  const selectedOption = !multi ? options.find((o) => o.value === value) || null : null;
  const selectedOptions = multi ? options.filter((o) => selectedSet.has(o.value)) : [];

  const handleSelect = (opt) => {
    if (multi) {
      const next = new Set(selectedSet);
      if (next.has(opt.value)) next.delete(opt.value);
      else next.add(opt.value);
      onChange(Array.from(next));
      // keep open for further selection
    } else {
      onChange(opt.value);
      setOpen(false);
    }
  };

  const handleCreate = async () => {
    const created = await onCreateOption(query.trim());
    if (!created) return;
    if (multi) {
      const next = new Set(selectedSet);
      next.add(created.value);
      onChange(Array.from(next));
    } else {
      onChange(created.value);
      setOpen(false);
    }
    setQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(multi ? [] : '');
  };

  return (
    <div ref={rootRef} className="relative">
      {triggerNode ? (
        <div onClick={() => setOpen((v) => !v)} className="cursor-pointer">
          {triggerNode}
        </div>
      ) : (
        <button
          type="button"
          className={triggerClassName || triggerCls}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">
            {multi ? (
              selectedOptions.length === 0 ? (
                <span className="text-gray-400">{placeholder}</span>
              ) : (
                <span className="flex flex-wrap items-center gap-1">
                  {selectedOptions.map((o) => (
                    <span
                      key={o.value}
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                      style={{ background: o.color || '#e2e8f0' }}
                    >
                      {o.label}
                    </span>
                  ))}
                </span>
              )
            ) : selectedOption ? (
              <>
                {renderIcon ? renderIcon(selectedOption) : null}
                <span className="truncate">{selectedOption.label}</span>
              </>
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </span>
          <span className="flex items-center gap-1 text-gray-400">
            {clearable && (multi ? selectedOptions.length > 0 : !!selectedOption) ? (
              <FaTimes size={10} onClick={handleClear} className="hover:text-gray-600" />
            ) : null}
            <FaChevronDown size={9} />
          </span>
        </button>
      )}

      {open ? (
        <div
          className={`absolute left-0 z-50 mt-1 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg ${
            popoverClassName || 'w-full min-w-[220px]'
          }`}
        >
          <div className="flex items-center gap-1 border-b border-gray-100 px-2 py-0.5">
            <FaSearch className="text-gray-400" size={9} />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canCreate) {
                  e.preventDefault();
                  handleCreate();
                }
              }}
              placeholder="Search"
              className="h-6 w-full bg-transparent text-xs text-gray-800 placeholder:text-gray-400 focus:outline-none"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && !canCreate ? (
              <div className="px-3 py-2 text-xs text-gray-400">{emptyLabel}</div>
            ) : null}
            {filtered.map((opt) => {
              const selected = selectedSet.has(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-selected={selected || undefined}
                  onClick={() => handleSelect(opt)}
                  className="reminders-neutral-btn flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm"
                >
                  {renderRow ? (
                    renderRow(opt, selected)
                  ) : (
                    <>
                      {renderIcon ? renderIcon(opt) : null}
                      <span className="flex-1 truncate">{opt.label}</span>
                      {opt.sublabel ? (
                        <span className="text-xs text-gray-400">{opt.sublabel}</span>
                      ) : null}
                      {selected ? <FaCheck className="text-green-600" size={11} /> : null}
                    </>
                  )}
                </button>
              );
            })}
            {canCreate ? (
              <button
                type="button"
                onClick={handleCreate}
                className="reminders-neutral-btn-create flex w-full items-center gap-2 text-left text-sm"
              >
                <FaPlus size={9} />
                Create &ldquo;{query.trim()}&rdquo;
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default SearchableSelect;
